import React from 'react';
import { Persona, Text, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import ChatListItemActions from './ChatListItemActions';
import EditChatName from './EditChatName';
import { AgentCapabilities } from '../../../domain/agents/models/AgentCapabilities';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { Breakpoints, customColors, customFonts } from '../../../styles';
import { buildAvatarSrc } from '../../utils/AvatarUtils';

interface ChatListItemProps {
    sessionId: string;
    title: string;
    isTitleGenerated?: boolean;
    providerType: ChatProviderType;
    timestampText: string;
    avatarImageName?: string;
    capabilities: AgentCapabilities;
    isSelected: boolean;
    forceExpanded?: boolean;
    onSelect: (sessionId: string) => void;
    onDelete: (sessionId: string) => Promise<void>;
    onRename: (sessionId: string, title: string) => Promise<void>;
    onDownload: (sessionId: string) => Promise<void>;
}

const useStyles = makeStyles({
    root: {
        boxSizing: 'border-box',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        cursor: 'pointer',
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXL),
        ...Breakpoints.small({
            justifyContent: 'center',
        }),
    },
    rootExpanded: {
        justifyContent: 'flex-start',
    },
    selected: {
        backgroundImage: 'linear-gradient(270deg, #01416B 66%, rgba(0, 64, 104, 0.1) 124.52%)',
    },
    body: {
        minWidth: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        marginLeft: tokens.spacingHorizontalXS,
        alignSelf: 'center',
    },
    bodyExpanded: {
        display: 'flex',
    },
    header: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        flexGrow: 1,
    },
    actionsInline: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: tokens.spacingHorizontalS,
    },
    title: {
        ...shorthands.overflow('hidden'),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '12.5px',
        fontFamily: customFonts.Lato,
        color: customColors.white,
        lineHeight: tokens.lineHeightBase200,
    },
    timestamp: {
        flexShrink: 0,
        marginLeft: tokens.spacingHorizontalM,
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
    },
    actions: {
        marginTop: tokens.spacingVerticalXS,
    },
    icon: {
        '& > span': {
            justifyContent: 'center',
            alignItems: 'center',
            display: 'inline-flex',
        },
        '& > span > img': {
            backgroundColor: 'transparent',
            width: '100%',
            height: '100%',
        },
    },
});

// TODO: Please implement the enabling/disabling of the chat renaming based on new implementation
// TODO: When chat is renamed, it needs to be set to the top of the chat list
const ChatListItem: React.FC<ChatListItemProps> = ({
    sessionId,
    title,
    // isTitleGenerated,
    providerType,
    timestampText: _timestampText,
    avatarImageName,
    capabilities,
    isSelected,
    forceExpanded,
    onSelect,
    onDelete,
    onRename,
    onDownload,
}) => {
    const styles = useStyles();
    const avatarSrc = buildAvatarSrc(providerType, avatarImageName);
    const [editingTitle, setEditingTitle] = React.useState(false);
    const [displayedTitle, setDisplayedTitle] = React.useState(title);
    const previousTitleRef = React.useRef(title);

    React.useEffect(() => {
        if (previousTitleRef.current === title) return;
        previousTitleRef.current = title;

        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        if (reducedMotion) {
            setDisplayedTitle(title);
            return;
        }

        let charIndex = 0;
        setDisplayedTitle('');

        const tick = (): void => {
            charIndex += 1;
            setDisplayedTitle(title.slice(0, charIndex));
            if (charIndex < title.length) {
                timeoutId = window.setTimeout(tick, 22);
            }
        };
        let timeoutId = window.setTimeout(tick, 22);

        return () => window.clearTimeout(timeoutId);
    }, [title]);

    const handleRename = async (nextTitle: string): Promise<void> => {
        await onRename(sessionId, nextTitle);
        setEditingTitle(false);
    };

    const handleClick = () => {
        onSelect(sessionId);
    };

    return (
        <div
            className={mergeClasses(
                styles.root,
                isSelected ? styles.selected : undefined,
                forceExpanded ? styles.rootExpanded : undefined,
            )}
            onClick={handleClick}
        >
            <Persona
                className={styles.icon}
                avatar={{
                    image: {
                        src: avatarSrc,
                    },
                    shape: 'square',
                }}
            />
            <div className={mergeClasses(styles.body, forceExpanded ? styles.bodyExpanded : undefined)}>
                <div className={styles.header}>
                    {editingTitle ? (
                        <EditChatName name={title} onSave={handleRename} onCancel={() => setEditingTitle(false)} />
                    ) : (
                        <>
                            <div className={styles.titleRow}>
                                <Text className={styles.title}>{displayedTitle}</Text>
                            </div>
                            {isSelected && (
                                <div className={styles.actionsInline} onClick={(event) => event.stopPropagation()}>
                                    <ChatListItemActions
                                        capabilities={capabilities}
                                        chatId={sessionId}
                                        chatName={title}
                                        // isTitleGenerated={isTitleGenerated}
                                        forceExpanded={forceExpanded}
                                        onEditTitleClick={() => setEditingTitle(true)}
                                        onDownloadChat={onDownload}
                                        onDeleteChat={onDelete}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatListItem;
